/**
 * Auth store — the REACTIVE PROJECTION of the `['auth','me']` server query.
 *
 * The SOURCE of truth is the TanStack Query cache (owned by `useAuthQuery`,
 * fetched/refreshed on staleTime/focus — the fix for the stale-user bug). This
 * store is a thin, provider-independent mirror of that cache, kept in sync both
 * ways (see the two subscriptions below):
 *   - cache → store: a background refetch / login / logout updates the store,
 *     so consumers re-render reliably (a plain query observer does NOT re-render
 *     across a mismatched QueryClientProvider, e.g. in tests — Zustand does).
 *   - store → cache: the legacy test idiom `useAuthStore.setState({ user })`
 *     also populates the cache the imperative accessors read.
 *
 * Read it in components via `useCurrentUser()` (which selects from this store);
 * mutate via `@/auth/authData` (`signIn`/`updateCurrentUser`/`clearCurrentUser`)
 * + `@/auth/session`. Tests seed with `getState().setUser()` / `.logout()`.
 * Do NOT import this store directly in production components — use
 * `useCurrentUser`.
 */
import { create } from 'zustand';
import type { User, UserContext } from '@/types';
import { registerStoreReset } from './registry';
import { queryClient } from '@/api/queryClient';
import {
  AUTH_ME_KEY,
  getCurrentUser,
  setCurrentUser,
  clearCurrentUser,
  updateCurrentUser,
  readSeed,
  isAdmin as isAdminOf,
  isManager as isManagerOf,
  isViewer as isViewerOf,
  isStaff as isStaffOf,
  isSuperuser as isSuperuserOf,
  canSwitchContext as canSwitchContextOf,
  currentContext as currentContextOf,
  requiresContextSelection as requiresContextSelectionOf,
} from '@/auth/authData';
import { getToken, setToken, clearToken, hasSession } from '@/auth/session';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;

  setUser: (user: User) => void;
  logout: () => void;
  updateUser: (updates: Partial<User>) => void;

  isAdmin: () => boolean;
  isManager: () => boolean;
  isViewer: () => boolean;
  isStaff: () => boolean;
  isSuperuser: () => boolean;

  canSwitchContext: () => boolean;
  currentContext: () => UserContext | null;
  requiresContextSelection: () => boolean;
}

/**
 * Initial user for this page load.
 *
 * The seed is a PAINT optimization (last-known user, so a reload doesn't flash
 * the login page) — never evidence of a session. The TOKEN is (`hasSession`).
 * Adopting the seed without one produced a signed-OUT visitor whose store said
 * `isAuthenticated: true`: ProtectedRoute let them into /dashboard, every
 * request went out tokenless, and the app ran with a user object that the very
 * next auth failure replaces with null — the stale-flag state behind the
 * "reads `.id` of a null user" rejection class (vingreact#82). main.tsx already
 * gates its cache priming on `hasSession()`; this is the same rule applied to
 * the store ProtectedRoute actually reads — and `useAuthQuery.initialData`, the
 * third seed-adoption site, is gated with it (without that one the seed simply
 * re-entered through the query cache and the cache→store mirror below).
 */
const seed = getCurrentUser() ?? (hasSession() ? readSeed() : null);

export const useAuthStore = create<AuthState>()((set, get) => ({
  user: seed,
  isAuthenticated: !!seed,

  setUser: (user) => {
    // /auth/me responses carry no token; inherit the live session token rather
    // than overwrite it with undefined (the "Token undefined" bug of old).
    const token = user.token ?? getToken();
    if (token) setToken(token);
    const next = (token !== undefined ? { ...user, token } : user) as User;
    setCurrentUser(next); // query cache is the source of truth (+ seed)
    set({ user: next, isAuthenticated: true });
  },

  logout: () => {
    clearToken();
    clearCurrentUser();
    set({ user: null, isAuthenticated: false });
  },

  updateUser: (updates) => {
    updateCurrentUser(updates); // ports the M4 org-derivation + F4 staff-override clear
    const next = getCurrentUser();
    set({ user: next, isAuthenticated: !!next });
  },

  isAdmin: () => isAdminOf(get().user),
  isManager: () => isManagerOf(get().user),
  isViewer: () => isViewerOf(get().user),
  isStaff: () => isStaffOf(get().user),
  isSuperuser: () => isSuperuserOf(get().user),

  canSwitchContext: () => canSwitchContextOf(get().user),
  currentContext: () => currentContextOf(get().user),
  requiresContextSelection: () => requiresContextSelectionOf(get().user),
}));

// Mirror the query cache into store state so selector-based consumers re-render
// when auth/me self-refreshes (focus/staleTime) or is updated imperatively.
queryClient.getQueryCache().subscribe((event) => {
  const key = event?.query?.queryKey;
  if (!Array.isArray(key) || key[0] !== AUTH_ME_KEY[0] || key[1] !== AUTH_ME_KEY[1]) return;
  const data = getCurrentUser();
  if (useAuthStore.getState().user !== data) {
    useAuthStore.setState({ user: data, isAuthenticated: !!data });
  }
});

// Mirror store → cache too, so the legacy test idiom
// `useAuthStore.setState({ user })` (used by hundreds of tests to seed auth)
// populates the query cache that migrated components now read via
// useCurrentUser. Guarded against the cache→store sync above so the two can't
// loop: each only writes when the two sides actually differ.
useAuthStore.subscribe((state) => {
  if (getCurrentUser() === state.user) return;
  if (state.user) {
    queryClient.setQueryData(AUTH_ME_KEY, state.user);
  } else {
    queryClient.removeQueries({ queryKey: AUTH_ME_KEY });
  }
});

// On global logout/reset: clear the token + the auth/me cache, then the store.
registerStoreReset(() => {
  clearToken();
  clearCurrentUser();
  useAuthStore.setState({ user: null, isAuthenticated: false });
}, { onUserSwitch: false }); // a switch keeps the token and writes the survivor itself (#1336)
